import React from "react";

/**
 * LoadingSpinner Component
 *
 * A reusable loading spinner with customizable size and optional text.
 * Uses CSS animation for smooth spinning effect.
 *
 * @param {Object} props
 * @param {string} props.size - Size of the spinner: 'small', 'medium', or 'large'
 * @param {string} props.text - Optional text to display below the spinner
 *
 * Usage:
 * <LoadingSpinner size="medium" text="Loading..." />
 */
export default function LoadingSpinner({ size = 'medium', text }) {
  // Size configurations (in pixels)
  const sizeMap = {
    small: 24,
    medium: 48,
    large: 72
  };

  const spinnerSize = sizeMap[size] || sizeMap.medium;

  return (
    <div
      className="loading-spinner-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
    >
      {/* Spinner */}
      <div
        className="spinner"
        style={{
          width: `${spinnerSize}px`,
          height: `${spinnerSize}px`,
          border: `${Math.max(2, spinnerSize / 12)}px solid #f3f3f3`,
          borderTop: `${Math.max(2, spinnerSize / 12)}px solid #7c8799`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}
        aria-label="Loading"
        role="status"
      />

      {/* Optional text */}
      {text && (
        <p
          className="loading-text"
          style={{
            marginTop: '0.75rem',
            color: '#666',
            fontSize: size === 'small' ? '0.875rem' : '1rem'
          }}
        >
          {text}
        </p>
      )}

      {/* Inline keyframes for the spinner animation (plain <style>: there is
          no styled-jsx here, so a `jsx` attribute is just an invalid DOM prop) */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
